import { useEffect, useLayoutEffect, useRef, useState, type MouseEvent } from "react";
import { useNavigate } from "react-router";
import { useStore } from "zustand";
import { formatText } from "../i18n/texts";
import { isMigrated } from "../shell/routes";
import { BheliLoader } from "../ui/brand";
import { SectionMessage, Toast } from "../ui/feedback";
import { ChatIcon } from "../ui/icons/Icon";
import { SelectionToolbar } from "../ui/SelectionToolbar";
import { ContactDetail } from "./ContactDetail";
import { ContactEditor } from "./ContactEditor";
import { ContactFilters } from "./ContactFilters";
import { ContactImportWizard } from "./ContactImportWizard";
import { fullName, isDateColumn, sortColumn, sortDescending, sortField } from "./contactModel";
import { ContactPagination, ContactTable } from "./ContactTable";
import { viewUrl } from "./contactViewState";
import { ContactWorkspaceController, type EditorHandle, type ImportWizardHandle } from "./contactWorkspaceController";
import styles from "./ContactWorkspace.module.css";
import { ManualCopyDialog, type ManualCopyHandle } from "./ManualCopyDialog";
import { count } from "./tagFormat";
import { bindWorkspace } from "./workspaceDom";

// Contactos del Chat (ContactWorkspace.razor del portal actual): lista, ficha al costado, editor, importar, copiar y
// exportar, con el mismo HTML, los mismos textos y las mismas direcciones. Los permisos los decide el servidor; la
// pantalla solo muestra lo que el contexto permite. La lógica vive en ContactWorkspaceController.

type Props = {
  labels: Record<string, string>;
  locale: string;
  tenant: number;
  user: number;
  id: number | null;
  edit: string | null;
  search: string;
  /** Cambia en cada navegación del enrutador (también Atrás y Adelante). */
  locationKey: string;
};

export function ContactWorkspace({ labels, locale, tenant, user, id, edit, search, locationKey }: Props) {
  const [ctl] = useState(() => new ContactWorkspaceController(labels, locale, tenant, user));
  const view = useStore(ctl.store);
  const navigate = useNavigate();
  const s = styles.scope;
  const root = useRef<HTMLElement>(null);
  const extractDialog = useRef<HTMLDialogElement>(null);
  const mobileSheet = useRef<HTMLDialogElement>(null);
  const searchInput = useRef<HTMLInputElement>(null);
  const copyDialog = useRef<ManualCopyHandle>(null);
  const sortTitle = useRef<HTMLSpanElement>(null);
  const sortEntry = useRef<HTMLButtonElement>(null);
  const importWizard = useRef<ImportWizardHandle>(null);
  const editor = useRef<EditorHandle>(null);

  // La dirección es la fuente de la consulta: cada navegación vuelve a leerla (OnParametersSetAsync).
  useLayoutEffect(() => {
    ctl.route((path, replace) => navigate(path, { replace }), id, edit, search);
  }, [ctl, navigate, id, edit, search, locationKey]);
  // Ordenar, filtrar y paginar agregan entradas al historial sin pasar por el enrutador (como en Blazor): Atrás y
  // Adelante entre ellas no cambian la ubicación que conoce el enrutador, así que se leen aquí. Como FocusOnNavigate,
  // el control activo pierde el foco.
  useEffect(() => {
    const popped = () => {
      const match = /^\/portal\/chat\/contactos(?:\/(\d+))?\/?$/i.exec(location.pathname);
      if (!match) return;
      const value = new URLSearchParams(location.search).get("edit");
      ctl.route(
        (path, replace) => navigate(path, { replace }),
        match[1] !== undefined ? Number(match[1]) : null,
        value,
        location.search,
      );
      const heading = document.querySelector<HTMLElement>("h1");
      heading?.focus();
      if (document.activeElement !== heading) (document.activeElement as HTMLElement | null)?.blur();
    };
    window.addEventListener("popstate", popped);
    return () => window.removeEventListener("popstate", popped);
  }, [ctl, navigate]);

  useEffect(() => {
    ctl.attach({
      extractDialog: extractDialog.current,
      mobileSheet: mobileSheet.current,
      searchInput: searchInput.current,
      copyDialog: copyDialog.current,
      importWizard: importWizard.current,
      editor: editor.current,
    });
  });
  useEffect(() => {
    ctl.revive();
    const unbind = bindWorkspace(root.current!, {
      longSelect: ctl.longSelect,
      navigateFromEditor: (path) => void ctl.navigateFromEditor(path),
      windowReturned: () => void ctl.windowReturned(),
      importClosed: ctl.importClosed,
      reportColumns: ctl.reportColumns,
      dismiss: ctl.dismiss,
      contentWidth: ctl.contentWidthChanged,
      loadMore: () => void ctl.loadMore(),
    });
    void ctl.bind();
    return () => {
      unbind();
      ctl.dispose();
    };
  }, [ctl]);
  useEffect(() => {
    ctl.afterRender(sortTitle.current, sortEntry.current);
  });
  useEffect(() => {
    document.title = `${ctl.L("TITLE")} · Bheli`;
  }, [ctl]);

  /** Los enlaces a pantallas ya migradas navegan dentro del portal, como la navegación mejorada de Blazor. */
  const followLink = (event: MouseEvent) => {
    if (
      event.defaultPrevented ||
      event.button !== 0 ||
      event.ctrlKey ||
      event.metaKey ||
      event.shiftKey ||
      event.altKey
    )
      return;
    const link = (event.target as Element).closest<HTMLAnchorElement>("a[href]");
    if (!link || link.target === "_blank" || link.hasAttribute("download") || link.dataset.enhanceNav === "false")
      return;
    const destination = new URL(link.href, location.href);
    if (destination.origin !== location.origin || !isMigrated(destination.pathname)) return;
    event.preventDefault();
    navigate(destination.pathname + destination.search + destination.hash);
  };

  const page = view.page;
  const refreshing = view.busy && (page !== null || view.detail !== null);
  // I13/C22 · en la primera carga de la lista la pantalla se arma con los permisos del contexto.
  const firstLoading =
    view.busy && page === null && view.detail === null && view.error === null && view.edit === null && view.id === null;
  const access = page?.access ?? view.contextRights;
  const query = view.query;
  const hasFilters =
    !!query.search?.trim() ||
    (query.tags?.length ?? 0) > 0 ||
    query.inboxId !== null ||
    query.agentUserId !== null ||
    query.period !== "ANY";
  const emptyLine =
    view.hiddenCount > 0
      ? formatText(
          ctl.L(view.hiddenCount === 1 ? "HIDDEN_RESULTS_ONE" : "HIDDEN_RESULTS"),
          view.hiddenCount,
          query.search ?? "",
        )
      : query.search
        ? formatText(ctl.L("SEARCH_EMPTY"), query.search)
        : hasFilters
          ? null
          : ctl.L("EMPTY");
  const total = page?.total ?? 0;
  const exportSummary = formatText(
    ctl.L("EXPORT_SUMMARY" + (hasFilters ? "_FILTERED" : "") + (total === 1 ? "_ONE" : "")),
    total,
  );
  const person = view.detail?.contact ?? view.preview?.contact ?? null;
  const rights = view.detail?.access ?? page?.access ?? null;
  const visible = ctl.visibleFields(view);
  const mobileSortOptions = ["Contact", ...visible.filter((name) => name !== "Tags")].map((name) => ({
    value: sortField(name),
    label: ctl.field(name),
  }));
  const draftColumn = sortColumn(view.sortDraft);
  const draftDirections = isDateColumn(draftColumn) ? [true, false] : [false, true];
  const canImport = access?.canImport === true && access.canWrite && access.globalScope;
  const canCreate = access?.canWrite === true && access.globalScope;

  return (
    <section
      className={`c1-workspace ${view.edit !== null ? "c1-editing" : ""} ${refreshing ? "c1-refreshing" : ""} ${s}`}
      ref={root}
      data-contact-workspace=""
      data-contact-tenant={tenant}
      aria-busy={view.busy ? "true" : "false"}
      onClick={followLink}
    >
      <div className={`page-header ${s}`}>
        <div className={`page-titles ${s}`}>
          {/* KH6 · C03: la ← va en la fila del título, también al volver a «Administrar etiquetas». */}
          {view.edit !== null ? (
            <a className={`c1-icon c1-back-action icon-button ${s}`} href={viewUrl(view.id)}>
              <ChatIcon name="arrow-left" />
              <span className={`sr-only ${s}`}>{ctl.L("BACK")}</span>
            </a>
          ) : view.returnTags !== null && view.id === null ? (
            <a
              className={`c1-icon c1-back-action icon-button ${s}`}
              href={view.returnTags}
              aria-label={ctl.L1("BACK_TAGS")}
              title={ctl.L1("BACK_TAGS")}
            >
              <ChatIcon name="arrow-left" />
            </a>
          ) : null}
          <h1 className={s}>
            {view.edit !== null ? (view.id !== null ? ctl.L("EDIT") : ctl.L("NEW")) : ctl.L("TITLE")}
          </h1>
        </div>
        {view.edit === null && (page !== null || (firstLoading && access !== null)) ? (
          <div className={`page-actions actions ${s}`}>
            {canImport ? (
              <button className={`button secondary c1-desktop-action ${s}`} onClick={ctl.importOpen}>
                <ChatIcon name="upload" />
                {ctl.L("IMPORT")}
              </button>
            ) : null}
            {canCreate ? (
              <a className={`button ${s}`} href={viewUrl(null, "crear")} onClick={ctl.saveList}>
                <ChatIcon name="plus" />
                {ctl.L("NEW")}
              </a>
            ) : null}
            <div className={`c1-mobile-overflow ${s}`}>
              <button
                className={`button ghost c1-icon icon-button ${s}`}
                aria-label={ctl.L("MORE_ACTIONS")}
                title={ctl.L("MORE_ACTIONS")}
                onClick={ctl.openMobileMenu}
              >
                <ChatIcon name="ellipsis-vertical" />
              </button>
            </div>
          </div>
        ) : null}
      </div>
      {view.scopeNotice !== null ? (
        <p className={`c2-scope-notice ${s}`} role="status">
          {view.scopeNotice}
        </p>
      ) : null}
      {view.error !== null ? (
        <>
          <SectionMessage message={view.error} tone="error" />
          <button className={`button secondary ${s}`} onClick={() => void ctl.load()} disabled={view.tenantChanged}>
            {ctl.L("RETRY")}
          </button>
        </>
      ) : null}
      {view.tenantChanged ? (
        <a className={`button secondary ${s}`} href={view.recoveryUrl} data-enhance-nav="false">
          {ctl.L(view.sessionEnded ? "SIGN_IN" : "RELOAD")}
        </a>
      ) : null}
      {refreshing || (firstLoading && view.slow) ? (
        // KH2 · C22: buscar, filtrar u ordenar no mueve nada: barra fina arriba y lo anterior atenuado.
        <div className={`c1-progress ${s}`} role="status" aria-label={ctl.L("LOADING")}></div>
      ) : view.busy && page === null && view.detail === null && view.error === null && !firstLoading ? (
        // I13/C22 · sin texto de carga; con lista armada el esqueleto va en la tabla, y aquí solo sin lista.
        <div className={`e1-contact-skeleton ${s}`} aria-hidden="true">
          {[0, 1, 2, 3, 4].map((row) => (
            <div key={row} className={`e1-skeleton-row ${s}`}>
              <div className={`e1-skeleton-line ${s}`}>
                <span className={`e1-skeleton-avatar ${s}`}></span>
                <span className={`e1-skeleton-bar ${s}`}></span>
                <span className={`e1-skeleton-bar e1-skeleton-short ${s}`}></span>
              </div>
            </div>
          ))}
        </div>
      ) : null}
      {view.edit !== null && !view.busy && view.error === null && (view.detail !== null || page !== null) ? (
        <ContactEditor
          key={view.id ?? "new"}
          ref={editor}
          ctl={ctl}
          contact={view.detail?.contact ?? null}
          rights={view.detail?.access ?? page?.access}
        />
      ) : (page !== null || firstLoading) && view.error === null ? (
        <>
          <div className={`c1-search-row ${s}`}>
            <div className={`c1-search ${s}`}>
              <ChatIcon name="search" />
              <input
                className={s}
                ref={searchInput}
                type="search"
                enterKeyHint="search"
                aria-label={ctl.L("SEARCH")}
                placeholder={ctl.L("SEARCH")}
                value={query.search ?? ""}
                onChange={(event) => void ctl.searchFor(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === "Escape") void ctl.clearSearch();
                }}
              />
              {query.search ? (
                <button
                  className={`c1-icon icon-button ${s}`}
                  aria-label={ctl.L("CLEAR_SEARCH")}
                  title={ctl.L("CLEAR_SEARCH")}
                  onClick={() => void ctl.clearSearch()}
                >
                  <ChatIcon name="x" />
                </button>
              ) : null}
            </div>
            <ContactFilters ctl={ctl} view={view} />
          </div>
          {page !== null && page.total > 0 ? (
            <>
              <SelectionToolbar
                count={view.selected.size}
                label={ctl.L("SELECTED_ACTIONS")}
                selected={
                  <>
                    <div className={`actions ${s}`}>
                      <button
                        className={`c1-icon icon-button ${s}`}
                        aria-label={ctl.L("CLEAR_SELECTION")}
                        title={ctl.L("CLEAR_SELECTION")}
                        onClick={() => ctl.clearSelection()}
                      >
                        <ChatIcon name="x" />
                      </button>
                      <strong className={s}>
                        {formatText(ctl.L(view.selected.size === 1 ? "SELECTED_ONE" : "SELECTED"), view.selected.size)}
                      </strong>
                    </div>
                    {page.access.canExport ? (
                      <button
                        className={`button secondary ${s}`}
                        onClick={() => void ctl.copy()}
                        disabled={view.copyBusy}
                        aria-busy={view.copyBusy ? "true" : "false"}
                      >
                        <ChatIcon name="copy" />
                        {ctl.L("COPY")}
                      </button>
                    ) : null}
                  </>
                }
              >
                <p className={s} role="status">
                  {formatText(ctl.L2(page.total === 1 ? "TOTAL_ONE" : "TOTAL"), count(page.total))}
                </p>
                <div className={`actions c1-desktop-action ${s}`}>
                  {page.access.canExport ? (
                    <button className={`button secondary ${s}`} onClick={ctl.exportOpen} disabled={view.extractBusy}>
                      <ChatIcon name="download" />
                      {ctl.L("EXPORT")}
                    </button>
                  ) : null}
                  <span className={s} data-report-launcher="chat.contacts"></span>
                </div>
              </SelectionToolbar>
              {view.copyError !== null ? (
                <div className={`c1-copy-error ${s}`}>
                  <SectionMessage message={view.copyError} tone="error" />
                  <div className={`actions ${s}`}>
                    {view.copyPageExpired ? (
                      <a
                        className={`button secondary ${s}`}
                        href={view.copyReloadUrl ?? viewUrl()}
                        data-enhance-nav="false"
                      >
                        {ctl.L("RELOAD")}
                      </a>
                    ) : (
                      <button
                        className={`button secondary ${s}`}
                        disabled={view.copyBusy || view.selected.size === 0}
                        onClick={() => void ctl.copy()}
                      >
                        {ctl.L("RETRY")}
                      </button>
                    )}
                    {view.copyText !== null ? (
                      <button className={`button ghost ${s}`} onClick={ctl.showManualCopy}>
                        {ctl.L("COPY_MANUAL")}
                      </button>
                    ) : null}
                  </div>
                </div>
              ) : null}
            </>
          ) : null}
          {/* I13/C22 · en la primera carga la barra ocupa su lugar con el conteo en esqueleto. */}
          {page === null ? (
            <SelectionToolbar count={0} label="" selected={null}>
              <p className={s} aria-hidden="true">
                <span className={`c1-total-skel ${s}`}></span>
              </p>
              <div className={`actions c1-desktop-action ${s}`}>
                {access?.canExport ? (
                  <button className={`button secondary ${s}`} disabled>
                    <ChatIcon name="download" />
                    {ctl.L("EXPORT")}
                  </button>
                ) : null}
                {/* J4 · C22 1.14.4: sin el lanzador de columnas, que en la barra cargada no se ve (nunca un botón suelto). */}
              </div>
            </SelectionToolbar>
          ) : null}
          {/* C8-00 · C18 1.11: con filtros, «Sin resultados» y «Limpiar filtros», sin línea en medio. */}
          {page !== null && page.total === 0 ? (
            <div className={`c1-empty ${s}`}>
              <h2 className={s}>{ctl.L(hasFilters ? "NO_RESULTS_TITLE" : "EMPTY_TITLE")}</h2>
              {emptyLine !== null ? <p className={s}>{emptyLine}</p> : null}
              {hasFilters ? (
                <button className={`button secondary ${s}`} onClick={() => void ctl.emptyRecovery()}>
                  {ctl.L(view.hiddenCount > 0 || !query.search ? "CLEAR_FILTERS" : "CLEAR_SEARCH")}
                </button>
              ) : canCreate ? (
                // KH8 · C18: el vacío sin filtros dice qué pasa y ofrece cómo empezar, según el permiso.
                <div className={`actions c1-empty-actions ${s}`}>
                  <a className={`button secondary ${s}`} href={viewUrl(null, "crear")} onClick={ctl.saveList}>
                    <ChatIcon name="plus" />
                    {ctl.L("NEW")}
                  </a>
                  {page.access.canImport ? (
                    <button className={`button secondary ${s}`} onClick={ctl.importOpen}>
                      <ChatIcon name="upload" />
                      {ctl.L("IMPORT")}
                    </button>
                  ) : null}
                </div>
              ) : null}
            </div>
          ) : (
            <ContactTable ctl={ctl} view={view} />
          )}
          {/* K4 · C17: en el celular la lista carga sola al bajar (25 en 25); escritorio sigue con paginado. */}
          {page !== null && page.total > 0 ? (
            <>
              <div className={`c1-mobile-more ${s}`} data-mobile-more="">
                {view.mobileLoading ? (
                  <div role="status" className={`c1-mobile-loading ${s}`}>
                    <BheliLoader size={24} label={ctl.L("LOADING_MORE")} />
                    <span className={s}>{ctl.L("LOADING_MORE")}</span>
                  </div>
                ) : view.mobileError !== null ? (
                  <div role="alert" className={`c1-mobile-error ${s}`}>
                    <span className={s}>{ctl.L("LOAD_MORE_FAILED")}</span>
                    <button type="button" className={`button secondary ${s}`} onClick={() => void ctl.retryLoadMore()}>
                      {ctl.L("RETRY")}
                    </button>
                  </div>
                ) : null}
              </div>
              <div className={`c1-mobile-end ${s}`} aria-hidden="true"></div>
              <div className={`c1-desktop-pagination ${s}`}>
                <ContactPagination ctl={ctl} page={page} />
              </div>
            </>
          ) : null}
        </>
      ) : null}
      {/* C9-02 · C23 1.12: la ficha se abre, cambia y cierra sin navegar; sale al instante con la fila. */}
      {view.edit === null && view.open !== null && person !== null && view.error === null ? (
        <aside className={`c1-detail-panel ${s}`} aria-label={fullName(person)} tabIndex={-1}>
          <header className={s}>
            <button
              type="button"
              className={`c1-icon c1-panel-mobile-back icon-button ${s}`}
              aria-label={ctl.L("BACK")}
              title={ctl.L("BACK")}
              onClick={() => void ctl.closeContact()}
            >
              <ChatIcon name="arrow-left" />
            </button>
            <h2 className={s}>{ctl.L("FIELD_CONTACT")}</h2>
            <div className={`actions ${s}`}>
              {rights?.canWrite === true ? (
                <a
                  className={`c1-icon icon-button ${s}`}
                  href={viewUrl(view.open, "editar")}
                  aria-label={ctl.L("EDIT")}
                  title={ctl.L("EDIT")}
                >
                  <ChatIcon name="pencil" />
                </a>
              ) : null}
              <button
                type="button"
                className={`c1-icon c1-panel-close icon-button ${s}`}
                aria-label={ctl.L("CLOSE")}
                title={ctl.L("CLOSE")}
                onClick={() => void ctl.closeContact()}
              >
                <ChatIcon name="x" />
              </button>
            </div>
          </header>
          <ContactDetail
            ctl={ctl}
            contact={person}
            value={view.detail}
            rights={rights}
            rowThread={view.preview?.conversation}
            error={view.detailError}
          />
        </aside>
      ) : null}
      {view.copied ? (
        <Toast
          key={"c" + view.copyReceipt}
          message={formatText(ctl.L(view.copiedCount === 1 ? "COPIED_ONE" : "COPIED"), view.copiedCount)}
        />
      ) : null}
      {/* C5-03 · C30: ventana sin pasos; en el celular, pantalla completa con ← y la acción principal a todo el ancho. */}
      <dialog className={`c1-dialog c1-window ${s}`} ref={extractDialog} aria-labelledby="c1-export-title">
        <div className={`c1-dialog-head ${s}`}>
          <button
            className={`button ghost c1-icon c1-dialog-back icon-button ${s}`}
            aria-label={ctl.L("BACK")}
            onClick={ctl.closeExport}
          >
            <ChatIcon name="arrow-left" />
          </button>
          <h2 className={s} id="c1-export-title">
            {ctl.L("EXPORT")}
          </h2>
          <button
            className={`button ghost c1-icon c1-dialog-close icon-button ${s}`}
            aria-label={ctl.L("CLOSE")}
            onClick={ctl.closeExport}
          >
            <ChatIcon name="x" />
          </button>
        </div>
        {/* C7-04: una sola frase dice qué se descarga, con el número. */}
        <p className={`c1-export-summary ${s}`}>{exportSummary}</p>
        <div className={`c1-format ${s}`} role="group" aria-labelledby="c1-export-format">
          <span className={`c1-field-label ${s}`} id="c1-export-format">
            {ctl.L("FORMAT")}
          </span>
          <div className={`c1-filter-options ${s}`}>
            {["CSV", "TSV"].map((format) => {
              const on = view.extractFormat === format;
              return (
                <button
                  key={format}
                  type="button"
                  className={`chip ${on ? "active" : ""} ${s}`}
                  aria-pressed={on ? "true" : "false"}
                  onClick={() => ctl.chooseFormat(format)}
                >
                  {on ? <ChatIcon name="check" /> : null}
                  {format}
                </button>
              );
            })}
          </div>
        </div>
        {view.extractError !== null ? <SectionMessage message={view.extractError} tone="error" /> : null}
        <div className={`actions ${s}`}>
          <button className={`button secondary c1-window-dismiss ${s}`} onClick={ctl.closeExport}>
            {ctl.L("CANCEL")}
          </button>
          <button
            className={`button ${s}`}
            disabled={view.extractBusy}
            aria-busy={view.extractBusy ? "true" : "false"}
            onClick={() => void ctl.download()}
          >
            {ctl.L("DOWNLOAD")}
          </button>
        </div>
      </dialog>
      {view.exportedMessage !== null ? <Toast key={"x" + view.exportReceipt} message={view.exportedMessage} /> : null}
      <ManualCopyDialog ref={copyDialog} labels={labels} content={view.copyText} />
      <ContactImportWizard ref={importWizard} ctl={ctl} />
      {/* K5 · C28: en el celular, ⋮ abre la hoja inferior con Ordenar por, Importar y Exportar. */}
      <dialog className={`c1-mobile-sheet ${s}`} ref={mobileSheet} aria-label={ctl.L("MORE_ACTIONS")}>
        <div className={`bh-sheet-handle ${s}`} aria-hidden="true">
          <span className={s}></span>
        </div>
        {!view.mobileSortOpen ? (
          <div className={s} role="menu">
            <button
              type="button"
              role="menuitem"
              className={`c1-sheet-item c1-sort-entry ${s}`}
              ref={sortEntry}
              onClick={ctl.openMobileSort}
            >
              <ChatIcon name="arrow-up-down" />
              <span className={`c1-sheet-text ${s}`}>
                <span className={s}>{ctl.L("SORT_BY")}</span>
                <small className={s}>{ctl.currentSortLabel(query.sort)}</small>
              </span>
              <ChatIcon name="chevron-right" />
            </button>
            {canImport ? (
              <button type="button" role="menuitem" className={`c1-sheet-item ${s}`} onClick={ctl.mobileImport}>
                <ChatIcon name="upload" />
                <span className={`c1-sheet-main ${s}`}>{ctl.L("IMPORT")}</span>
              </button>
            ) : null}
            {access?.canExport === true ? (
              <button type="button" role="menuitem" className={`c1-sheet-item ${s}`} onClick={ctl.mobileExport}>
                <ChatIcon name="download" />
                <span className={`c1-sheet-main ${s}`}>{ctl.L("EXPORT")}</span>
              </button>
            ) : null}
          </div>
        ) : (
          // KA7 · C28 1.13: columnas de la vista con ✓ y «Orden» con dos píldoras; nada se ordena hasta «Aplicar».
          <div className={`c1-sort-sheet ${s}`} role="group" aria-label={ctl.L("SORT_BY")}>
            <div className={`c1-sheet-head ${s}`}>
              <button
                type="button"
                className={`button ghost c1-icon icon-button ${s}`}
                aria-label={ctl.L("BACK")}
                title={ctl.L("BACK")}
                onClick={ctl.closeMobileSort}
              >
                <ChatIcon name="arrow-left" />
              </button>
              <span className={`c1-sheet-title ${s}`} tabIndex={-1} ref={sortTitle}>
                {ctl.L("SORT_BY")}
              </span>
            </div>
            <div className={`c1-sort-columns ${s}`} role="radiogroup" aria-label={ctl.L("SORT_BY")}>
              {mobileSortOptions.map((option) => {
                const selected = draftColumn === option.value;
                return (
                  <button
                    key={option.value}
                    type="button"
                    className={`c1-sheet-item c1-sort-column ${s}`}
                    role="radio"
                    aria-checked={selected ? "true" : "false"}
                    onClick={() => ctl.pickMobileColumn(option.value)}
                  >
                    <span className={`c1-sheet-main ${s}`}>{option.label}</span>
                    {selected ? <ChatIcon name="check" /> : null}
                  </button>
                );
              })}
            </div>
            <div className={`c1-sort-order ${s}`}>
              <span className={`c1-sort-order-label ${s}`} id="c1-sort-order-label">
                {ctl.L("ORDER_LABEL")}
              </span>
              <div className={`c1-sort-pills ${s}`} role="radiogroup" aria-labelledby="c1-sort-order-label">
                {draftDirections.map((descending) => (
                  <button
                    key={String(descending)}
                    type="button"
                    className={`c1-sort-pill ${s}`}
                    role="radio"
                    aria-checked={sortDescending(view.sortDraft) === descending ? "true" : "false"}
                    onClick={() => ctl.pickMobileDirection(descending)}
                  >
                    {ctl.orderLabel(draftColumn, descending)}
                  </button>
                ))}
              </div>
            </div>
            <div className={`c1-sort-apply ${s}`}>
              <button type="button" className={`button ${s}`} onClick={() => void ctl.applyMobileSort()}>
                {ctl.L("APPLY")}
              </button>
            </div>
          </div>
        )}
      </dialog>
    </section>
  );
}
